import { useState, useEffect, use } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { PieChart } from '@mui/x-charts/PieChart';
import DevicesIcon from '@mui/icons-material/Devices';
import { useMediaQuery, useTheme } from '@mui/system';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import SimCardDownloadSharpIcon from '@mui/icons-material/SimCardDownloadSharp';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Local imports
import VideoTabPanel from './VideoTabPanel';
import Error from "../Error/Error";
import { useAuth } from '../AuthContext'

import { api } from '../../api';
import Loading from '../Loading';

// Import styles
import { analyticsBoxStyles, iconStyles, metricsBoxStyles, metricsContainerStyles } from './VideoTabPanel';

// Env variable
const SERVER_URL = process.env.REACT_APP_SERVER_URL;

export const topBoxStyles = {
    background: 'linear-gradient(to right, #4e2a84, #a495c3)',
    color: 'white',
    padding: { xs: '10px', sm: '20px' },
    borderRadius: 2,
    textAlign: 'center',
    width: '30%',
}

export const containerStyles = {
    width: {
        xs: '100%',
        sm: '90%', 
    },
    margin: '0 auto', 
    marginTop: {xs: 2, sm: 4 }
}

const pieChartColors = { 0: '#4facfe', 1: '#764ba2', 2: '#ed4e5d'}

const a11yProps = (index) => {
  return {
    id: `video-tab-${index}`,
    'aria-controls': `video-tabpanel-${index}`,
  };
}

// Convert seconds to minutes and seconds
export const convertSecToMinAndSec = (sec) => {
    if (!sec) return 0;
    const minutes = Math.floor(sec / 60);
    const seconds = sec >= 60 ? sec % 60 : sec;
    return (minutes ? `${minutes} m ` : '') + (seconds ? `${Math.floor(seconds)} s` : '');
    // return (minutes ? minutes + ':' : '') + Math.floor(seconds);
}

// Info icon that opens a tooltip on click (instead of hover)
const InfoTooltip = ({ title }) => {
    const [open, setOpen] = useState(false);

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Tooltip
                title={title}
                arrow
                open={open}
                onClose={() => setOpen(false)}
                disableFocusListener
                disableHoverListener
                disableTouchListener
                slotProps={{
                    popper: { disablePortal: true },
                }}
            >
                <IconButton
                    size="small"
                    aria-label="Column description"
                    onClick={() => setOpen((prev) => !prev)}
                    sx={{ ml: 0.5, verticalAlign: 'middle' }}
                >
                    <InfoOutlinedIcon fontSize="inherit" />
                </IconButton>
            </Tooltip>
        </ClickAwayListener>
    );
}

const Dashboard = () => {
    const [totalParticipants, setTotalParticipants] = useState(0);
    const [totalViews, setTotalViews] = useState({});
    const [uniqueViews, setUniqueViews] = useState({});
    const [totalWatchTime, setTotalWatchTime] = useState(0);
    const [watchTimeByUser, setWatchTimeByUser] = useState({});
    const [avgWatchTime, setAvgWatchTime] = useState(0);
    const [watchTimePerVideo, setWatchTimePerVideo] = useState({});
    const [avgWatchTimePerUser, setAvgWatchTimePerUser] = useState({});
    const [deviceTypeData, setDeviceTypeData] = useState({});
    const [playsCompletionRate, setPlaysCompletionRate] = useState({});
    const [usersCompletionRate, setUsersCompletionRate] = useState({});
    const [assignedVideosByUsers, setAssignedVideosByUsers] = useState([]);
    const [vaccineInfoVisitors, setVaccineInfoVisitors] = useState(null);
    const [pdfScrollDepthByUser, setPdfScrollDepthByUser] = useState(null);
    const [pdfOpeners, setPdfOpeners] = useState(null);
    const [videos, setVideos] = useState([]);
    const [videoTitle1, setVideoTitle1] = useState('');
    const [videoTitle2, setVideoTitle2] = useState('');
    const [videoTitle3, setVideoTitle3] = useState('');
    const [tabValue, setTabValue] = useState(0);
    const [viewsChartData, setViewsChartData] = useState([]);
    const [watchTimeChartData, setWatchTimeChartData] = useState([]);
    const [completionChartData, setCompletionChartData] = useState([]);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingTable, setLoadingTable] = useState(true);
    const [error, setError] = useState('');

    const { isAdmin, authLoading } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const fetchVideoMetrics = async () => {
            try {
                const data = await api.get('/activity/videos');
                setTotalViews(data.totalViews);
                setUniqueViews(data.uniqueViews);
                setAvgWatchTime(data.avgDurationWatched);
                const watchTime = Object.fromEntries(
                    Object.entries(data.durationWatched).map(([videoId, duration]) => [videoId, Math.floor(duration)])
                );
                const avgWatchTime = Object.fromEntries(
                    Object.entries(data.durationWatched).map(([videoId, duration]) => [videoId, Math.floor(duration / (data.watchCount[videoId] || 1))])
                );
                setWatchTimePerVideo({ total: watchTime, avg: avgWatchTime });
            } catch (err) {
                console.error("Error fetching views by video:", err.message);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchVideoMetrics();
    }, []);

    useEffect(() => {
        const getWatchTime = async () => {
            try {
                const data = await api.get('/activity/watch-time');
                setTotalWatchTime(data.totalTime);
                setAvgWatchTimePerUser(data.avgWatchTimePerUser);
            } catch (err) {
                console.error("Error fetching watch time metrics", err.message);
                setError(err.message);
            }
        };

        getWatchTime();
    }, []);

    useEffect(() => {
        const getWatchTimeByUser = async () => {
            try {
                const data = await api.get('/activity/users/watch-time');
                setWatchTimeByUser(data);
            } catch (err) {
                console.error("Error fetching total watch time per user", err.message);
                setError(err.message);
            }
        };

        getWatchTimeByUser();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await api.get('/activity/device-type');
                setDeviceTypeData(data);
            }
            catch (err) {
                console.error("Error fetching device type data:", err.message);
                setError(err.message);
            }
        }

        fetchData();
    }, [])

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await api.get('/activity/videos/completion-rate');
                setPlaysCompletionRate(data);
            }
            catch (err) {
                console.error("Error fetching completion rate based on plays:", err.message);
                setError(err.message);
            }
        }

        fetchData();
    }, [])

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await api.get('/activity/users/completion-rate');
                setUsersCompletionRate(data);
            }
            catch (err) {
                console.error("Error fetching users based completion rate:", err.message);
                setError(err.message);
            }
        }

        fetchData();
    }, [])

    useEffect(() => {
        const fetchVideos = async () => {
            try {
                const data = await api.get('/videos/all');
                setVideos(data);

                // Set video titles
                const video1 = data.find(v => v.id === 1);
                const video2 = data.find(v => v.id === 2);
                const video3 = data.find(v => v.id === 3);
                setVideoTitle1(video1 ? video1.title : '');
                setVideoTitle2(video2 ? video2.title : '');
                setVideoTitle3(video3 ? video3.title : '');
            }
            catch (err) {
                console.error("Error fetching videos:", err.message);
                setError(err.message);
            }
        }

        fetchVideos();
    }, [])

    useEffect(() => {
        const fetchParticipants = async () => {
            try {
                const data = await api.get('/participants/total');
                setTotalParticipants(data);
            } catch (err) {
                console.error("Error fetching the number of participants", err.message);
                setError(err.message);
            }
        };

        fetchParticipants();
    }, []);

    useEffect(() => {
        if (!totalViews || !uniqueViews || !videos) return;
        const chartData = videos.map((video) => {
            return {
                title: video.title,
                totalViews: totalViews[video.id] || 0,
                uniqueViews: uniqueViews[video.id] || 0,
            };
        })
        setViewsChartData(chartData);
    }, [totalViews, uniqueViews, videos]);

    useEffect(() => {
        if (!playsCompletionRate || !usersCompletionRate || !videos) return;
        if (Object.keys(usersCompletionRate).length > 0 && Object.keys(playsCompletionRate).length > 0) {
            const chartData = videos.map((video) => {
                return {
                    title: video.title,
                    byPlays: playsCompletionRate[video.id] || 0,
                    byViewers: usersCompletionRate[video.id].viewers || 0,
                    byAssignedUsers: usersCompletionRate[video.id].assigned_users || 0,
                };
            })
            setCompletionChartData(chartData);
        }
    }, [playsCompletionRate, usersCompletionRate, videos]);

    useEffect(() => {
        if (!watchTimePerVideo.total || !watchTimePerVideo.avg || !videos) return;
        if (Object.keys(watchTimePerVideo.total).length > 0 && Object.keys(watchTimePerVideo.avg).length > 0) {
            const chartData = videos.map((video) => {
                return {
                    title: video.title,
                    total: watchTimePerVideo.total[video.id] || 0,
                    avg: watchTimePerVideo.avg[video.id] || 0,
                };
            })
            setWatchTimeChartData(chartData);
        }
    }, [watchTimePerVideo, videos]);

    useEffect(() => {
        const fetchAssignedVideos = async () => {
            try {
                const data = await api.get('/activity/users/assigned-videos');
                setAssignedVideosByUsers(data);
            } catch (err) {
                console.error("Error fetching the number of participants", err.message);
                setError(err.message);
            }
        };
        fetchAssignedVideos();
    }, []);

    useEffect(() => {
        const fetchVaccineInfoVisitors = async () => {
            try {
                const data = await api.get('/activity/users/vaccine-info-visits');
                setVaccineInfoVisitors(data);
            } catch (err) {
                console.error("Error fetching vaccine info page visitors", err.message);
                setError(err.message);
            }
        };
        fetchVaccineInfoVisitors();
    }, []);

    useEffect(() => {
        const fetchPdfScrollDepth = async () => {
            try {
                const data = await api.get('/activity/users/pdf-scroll-depth');
                setPdfScrollDepthByUser(data);
            } catch (err) {
                console.error("Error fetching PDF scroll depth per user", err.message);
                setError(err.message);
            }
        };
        fetchPdfScrollDepth();
    }, []);

    useEffect(() => {
        const fetchPdfOpeners = async () => {
            try {
                const data = await api.get('/activity/users/pdf-opens');
                setPdfOpeners(data);
            } catch (err) {
                console.error("Error fetching PDF openers", err.message);
                setError(err.message);
            }
        };
        fetchPdfOpeners();
    }, []);

    // Constructs table data
    useEffect(() => {
        if (!watchTimeByUser || !avgWatchTimePerUser) {
            setLoadingTable(true);
            return;
        }

        if (!Array.isArray(assignedVideosByUsers) || assignedVideosByUsers.length === 0) {
            return;
        }
        // Wait for the activity fetches (null = not loaded; empty is a valid result)
        if (vaccineInfoVisitors === null || pdfScrollDepthByUser === null || pdfOpeners === null) {
            return;
        }

        // Iterate the full participant roster (not the watch-time map) so that
        // participants with no watch activity still get a row.
        const tableData = assignedVideosByUsers.map(({ username, videos_in_order }) => {
            const data = watchTimeByUser[username] || {};
            const totalWatchTime = Object.values(data).reduce((acc, v) => acc + v.durationWatched, 0);
            const v1 = videos_in_order?.find(v => v.id === 1);
            const v2 = videos_in_order?.find(v => v.id === 2);
            const v3 = videos_in_order?.find(v => v.id === 3);
            return {
                username,
                totalWatchTime: convertSecToMinAndSec(totalWatchTime) || '-',
                avgWatchTime: convertSecToMinAndSec(avgWatchTimePerUser[username]) || '-',
                watchTimeVideo1: data['1']?.durationWatched ? convertSecToMinAndSec(data['1'].durationWatched) : '-',
                watchTimeVideo2: data['2']?.durationWatched ? convertSecToMinAndSec(data['2'].durationWatched) : '-',
                watchTimeVideo3: data['3']?.durationWatched ? convertSecToMinAndSec(data['3'].durationWatched) : '-',
                viewsVideo1: data['1']?.views || '-',
                viewsVideo2: data['2']?.views || '-',
                viewsVideo3: data['3']?.views || '-',
                completedVideo1: v1 ? (v1.completed ? 'Yes' : 'No') : 'Not Assigned',
                completedVideo2: v2 ? (v2.completed ? 'Yes' : 'No') : 'Not Assigned',
                completedVideo3: v3 ? (v3.completed ? 'Yes' : 'No') : 'Not Assigned',
                visitedVaccineInfo: vaccineInfoVisitors.includes(username) ? 'Yes' : 'No',
                // Scrolling the inline viewer or clicking the download link both count as viewing
                viewedPdf: (pdfOpeners.includes(username) || pdfScrollDepthByUser[username] !== undefined) ? 'Yes' : 'No',
                pdfScrollDepth: pdfScrollDepthByUser[username] !== undefined ? `${pdfScrollDepthByUser[username]}%` : '-',
            }
        });
        // Stable ordering across reloads
        tableData.sort((a, b) => a.username.localeCompare(b.username));
        setRows(tableData);
        setLoadingTable(false);
    }, [watchTimeByUser, avgWatchTimePerUser, assignedVideosByUsers, vaccineInfoVisitors, pdfScrollDepthByUser, pdfOpeners]);

    const calcTotalViews = (views) => {
        if (!views) return 0;
        return Object.values(views).reduce((total, count) => total + count, 0);
    }

    const handleTabChange = (ev, newVal) => {
        setTabValue(newVal);
    }

    const exportData = async () => {
        try {
            const response = await fetch(`${SERVER_URL}/activity/export-data`, {
                credentials: 'include',
            });
            if (!response.ok) {
                setError('Failed to export data');
                return;
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            // Create a link and trigger download
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'user_activity_data.csv');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (err) {
            console.error("Error exporting data:", err.message);
            setError(err.message);
        }
    }

    if (authLoading) {
        return <Loading />;
    }
    if (!isAdmin) {
        return <Error message="You do not have permission to view this page." />;
    }

    if (error) {
        return <Error message={error} />;
    }

    return (
        <Box sx={{background: 'linear-gradient(0.25turn, rgb(078, 042, 132), #caceab, #836EAA)', padding: {xs: 2, sm: 4 }, mt: {xs: 2, sm: 4 } }}>
            <Box sx={{ ...containerStyles, borderRadius: 2, padding: {xs: 1, sm: 3 }, backgroundColor: '#f5f5f5' }}>   
                <Typography variant={isMobile ? 'h5' : 'h3'} sx={{ textAlign: 'center', marginBottom: { xs: 2, sm: 4 }, fontWeight: '700', color: 'rgb(118, 93, 160)' }}>User Activity Dashboard</Typography>

                {/* Top metrics */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                    <Box sx={topBoxStyles}>
                        <Typography variant={isMobile ? 'subtitle1' : 'h5'} sx={{ fontWeight: '600' }}>{totalParticipants}</Typography>
                        <Typography variant={isMobile ? 'subtitle1' : 'h6'}>Total Participants</Typography>
                    </Box>
                    <Box sx={topBoxStyles}>
                        <Typography variant={isMobile ? 'subtitle1' : 'h5'} sx={{ fontWeight: '600' }}>{calcTotalViews(totalViews)}</Typography>
                        <Typography variant={isMobile ? 'subtitle1' : 'h6'}>Total Plays</Typography>
                    </Box>
                    <Box sx={topBoxStyles}>
                        <Typography variant={isMobile ? 'subtitle1' : 'h5'} sx={{ fontWeight: '600' }}>{convertSecToMinAndSec(avgWatchTime)}</Typography>
                        <Typography variant={isMobile ? 'subtitle1' : 'h6'}>Average Watch Time</Typography>
                    </Box>
                </Box>
            </Box>
            <Box sx={containerStyles}>
                <Button 
                    variant="outlined" 
                    startIcon={<SimCardDownloadSharpIcon />} 
                    onClick={exportData}
                    sx={{ backgroundColor: '#ffff', margin: '20px auto 0 auto' }}
                >
                    Export Data as CSV
                </Button>
            </Box>
            <Box sx={containerStyles}>
                {/* Video Tabs */}
                <Tabs indicatorColor='rgb(0, 0, 0, 0)' variant="scrollable" scrollButtons="auto" value={tabValue} onChange={handleTabChange} aria-label="video tabs">
                    <Tab label="All Videos" {...a11yProps(0)} />
                    {videos.length > 0 ?
                        videos.map((video, idx) => {
                            return (
                                <Tab label={video.title} key={video.id} {...a11yProps(idx+1)}/>
                            )
                        })
                    : 'No videos found.'}
                </Tabs>
            </Box>
            <VideoTabPanel
                value={tabValue} 
                index={0}
                data={{
                    totalViews: calcTotalViews(totalViews) || 0,
                    uniqueViews: calcTotalViews(uniqueViews) || 0,
                    avgWatchTime: avgWatchTime || 0,
                    totalWatchTime: totalWatchTime || 0,
                    playsCompletionRate: playsCompletionRate.total || {},
                    usersCompletionRate: usersCompletionRate ? {viewers: usersCompletionRate.total_views, assigned_users: null } : {},
                }}
                viewsChartData={viewsChartData}
                completionChartData={completionChartData}
                watchTimeChartData={watchTimeChartData}
            >
                All Videos
            </VideoTabPanel>
            {videos.length > 0 && videos.map((video, index) => {
                return (
                    <VideoTabPanel 
                        value={tabValue} 
                        index={index + 1} 
                        key={video.id} 
                        data={{
                            id: video.id,
                            totalViews: totalViews ? totalViews[video.id] : 0,
                            uniqueViews: uniqueViews ? uniqueViews[video.id] : 0,
                            avgWatchTime: watchTimePerVideo?.avg?.[video.id] || 0,
                            totalWatchTime: watchTimePerVideo?.total?.[video.id] || 0,
                            playsCompletionRate: playsCompletionRate?.[video.id] || 0,
                            usersCompletionRate: usersCompletionRate?.[video.id] || 0,
                        }}
                        viewsChartData={viewsChartData}
                        completionChartData={completionChartData}
                        watchTimeChartData={watchTimeChartData}
                    >
                        {video.title}
                    </VideoTabPanel>
                )
            })}
            <Box sx={containerStyles}>
                <Box sx={analyticsBoxStyles}>
                    <Typography variant='h6' sx={{ fontWeight: '600'}}><DevicesIcon sx={iconStyles}/>Device Usage</Typography>
                    <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
                        Number of video watch events logged from each device type
                    </Typography>
                    <Box sx={metricsContainerStyles}>
                        {deviceTypeData && Object.keys(deviceTypeData).length > 0 ? 
                            Object.entries(deviceTypeData).map(([device, data]) => (
                            <Box sx={metricsBoxStyles} key={device}>
                                <Typography variant='h5' sx={{ fontWeight: '600' }}>{data}</Typography>
                                <Typography variant='body1'>{device[0].toUpperCase() + device.slice(1).toLowerCase()}</Typography>
                            </Box> 
                            )
                        )
                        : 'No device data available.'}
                    </Box>
                    {deviceTypeData && Object.keys(deviceTypeData).length > 0 ? 
                        <PieChart
                            series={[
                                {
                                data: Object.entries(deviceTypeData).map(([device, data], idx) => {
                                    return { id: idx, value: data, label: device[0].toUpperCase() + device.slice(1).toLowerCase(), color: pieChartColors[idx] || '#4facfe' };
                                })
                                },
                            ]}
                            width={200}
                            height={200}
                            />
                    : 'No device data available.'}
                </Box>
            </Box>
            {/* User Based Analytics */}
            <Box sx={{ ...containerStyles, borderRadius: 2, padding: {xs: 1, sm: 3 }, backgroundColor: '#f5f5f5' }}>
                <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ textAlign: 'center', marginBottom: { xs: 2, sm: 4 }, fontWeight: '700', color: 'rgb(118, 93, 160)' }}>User-Based Analytics</Typography>
                {loadingTable ? <Loading /> :
                    <TableContainer component={Paper} sx={{ maxHeight: 1000 }}>
                        <Table stickyHeader sx={{ minWidth: 650 }} aria-label="User based analytics table">
                            <TableHead>
                                <TableRow sx={{ verticalAlign: 'top' }}>
                                    <TableCell scope="col" sx={{ position: 'sticky', left: 0, zIndex: 3, }}>Username</TableCell>
                                    <TableCell scope="col" align="center">Total Watch Time</TableCell>
                                    <TableCell scope="col" align="center">Average Watch Time</TableCell>
                                    <TableCell scope="col" align="center">Watch Time - <Typography variant="caption"><em>{videoTitle1}</em></Typography></TableCell>
                                    <TableCell scope="col" align="center">Watch Time - <Typography variant="caption"><em>{videoTitle2}</em></Typography></TableCell>
                                    <TableCell scope="col" align="center">Watch Time - <Typography variant="caption"><em>{videoTitle3}</em></Typography></TableCell>
                                    <TableCell scope="col" align="center"># of Views - <Typography variant="caption"><em>{videoTitle1}</em></Typography></TableCell>
                                    <TableCell scope="col" align="center"># of Views - <Typography variant="caption"><em>{videoTitle2}</em></Typography></TableCell>
                                    <TableCell scope="col" align="center"># of Views - <Typography variant="caption"><em>{videoTitle3}</em></Typography></TableCell>
                                    <TableCell scope="col" align="center">Completed - <Typography variant="caption"><em>{videoTitle1}</em></Typography></TableCell>
                                    <TableCell scope="col" align="center">Completed - <Typography variant="caption"><em>{videoTitle2}</em></Typography></TableCell>
                                    <TableCell scope="col" align="center">Completed - <Typography variant="caption"><em>{videoTitle3}</em></Typography></TableCell>
                                    <TableCell scope="col" align="center">Visited Vaccine Information page</TableCell>
                                    <TableCell scope="col" align="center">
                                        Viewed CDC Guidelines PDF
                                        <InfoTooltip title="Shows whether the participant scrolled the PDF viewer on the vaccine information page or opened the PDF via the download link" />
                                    </TableCell>
                                    <TableCell scope="col" align="center">
                                        Max PDF Scroll Depth
                                        <InfoTooltip title="The furthest point the participant scrolled in the PDF viewer, shown as a percentage of the document (25/50/75/100). Scrolling is only measurable in the on-page viewer, not in PDFs opened via the download link" />
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                            {rows.map((row, i) => (
                                <TableRow
                                key={row.username}
                                sx={{ 
                                    '&:last-child td, &:last-child th': { border: 0 },
                                    backgroundColor: i % 2 === 0 ? 'white' : '#f5f5f5',
                                }}
                                >
                                    <TableCell 
                                        component="th" 
                                        scope="row" 
                                        sx={{ 
                                            position: 'sticky', 
                                            left: 0, 
                                            zIndex: 1, 
                                            top: 10, 
                                            backgroundColor: i % 2 === 0 ? 'white' : '#f5f5f5', 
                                        }}
                                    >
                                        {row.username}
                                    </TableCell>
                                    <TableCell align="right">{row.totalWatchTime}</TableCell>
                                    <TableCell align="right">{row.avgWatchTime}</TableCell>
                                    <TableCell align="right">{row.watchTimeVideo1}</TableCell>
                                    <TableCell align="right">{row.watchTimeVideo2}</TableCell>
                                    <TableCell align="right">{row.watchTimeVideo3}</TableCell>
                                    <TableCell align="right">{row.viewsVideo1}</TableCell>
                                    <TableCell align="right">{row.viewsVideo2}</TableCell>
                                    <TableCell align="right">{row.viewsVideo3}</TableCell>
                                    <TableCell align="center">{row.completedVideo1}</TableCell>
                                    <TableCell align="center">{row.completedVideo2}</TableCell>
                                    <TableCell align="center">{row.completedVideo3}</TableCell>
                                    <TableCell align="center">{row.visitedVaccineInfo}</TableCell>
                                    <TableCell align="center">{row.viewedPdf}</TableCell>
                                    <TableCell align="center">{row.pdfScrollDepth}</TableCell>
                                </TableRow>
                            ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                }
            </Box>
        </Box>
    );
}

export default Dashboard;